import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/reports/audit-discrepancies")({
  component: AuditDiscrepanciesReport,
});

function AuditDiscrepanciesReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "audit-discrepancies", currentBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (currentBranchId) p.set("branch", currentBranchId);
      const res = await apiGet(`/reports/audit-discrepancies/?${p.toString()}`);
      return res as any[];
    }
  });

  const exportRows = useMemo(() => () => (data ?? []).map((r) => ({
    "Audit": r.audit_name,
    "Asset Code": r.asset_code,
    "Expected Loc": r.expected_location,
    "Found Loc": r.scanned_location,
    "Discrepancy": r.discrepancy_reason,
    "Date": r.scanned_at ? format(new Date(r.scanned_at), "PP") : "—",
  })), [data]);

  return (
    <ReportShell
      id="R-19"
      title="Physical Audit Result & Discrepancy Report"
      description="Findings and reconciliation from physical audits."
      filters={filters}
      onFiltersChange={setFilters}
      onExport={exportRows}
      exportFilename="Audit_Discrepancies"
      allowedFilters={["branchId"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Audit</TableHead>
                  <TableHead>Asset Code</TableHead>
                  <TableHead>Expected Loc</TableHead>
                  <TableHead>Found Loc</TableHead>
                  <TableHead>Discrepancy</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No data found</TableCell></TableRow>
                )}
                {data?.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.audit_name}</TableCell>
                    <TableCell>{r.asset_code}</TableCell>
                    <TableCell>{r.expected_location}</TableCell>
                    <TableCell>{r.scanned_location}</TableCell>
                    <TableCell className="text-destructive font-medium">{r.discrepancy_reason}</TableCell>
                    <TableCell>{r.scanned_at ? format(new Date(r.scanned_at), "PP") : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
